import React from 'react';
import { gql, useApolloClient } from '@apollo/client';
import SimpleButton from '../components/basicComponents/SimpleButton';
import { ValidationContext } from '../components/validation/ValidationProvider';
import ShareholderData from './ShareholderData';
import EarlyVoteForm from './EarlyVoteForm';
import PopUpWrapper from './PopUpWrapper';
import SendFormPopUp from '../components/popUps/SendFormPopUp';
import { getText } from '../utils/functions';
import ClosedSection from '../components/complexComponents/ClosedSection';
import RepresentativeOrDelegateForm from './RepresentativeOrDelegateForm';
import { MainAppContext } from '../MainAppContext';
import { useWindowDimensions } from '../utils/hooks';
import { WINDOW_SIZE } from '../utils/constants';
import AditionalText from '../components/basicComponents/AditionalText';
import InterventionsForm from './InterventionsForm';
import Representatives from '../components/basicComponents/Representatives';

function FormFactory({
	formType,
	colors,
	portal,
	onSuccess,
	translate,
	dateString,
	updateRouting,

	/* OPTIONAL PROPS */
	classNameDIV = 'flex flex-col max-w-screen-2xl md:mx-auto space-y-4 font-lato pt-14 lg:pl-16 lg:pr-16 md:pl-10 md:pr-10 sm:pl-4 sm:pr-4 mx-3 pb-10',
}) {
	/* GraphQL client */
	const client = useApolloClient();

	/* */
	const { width } = useWindowDimensions();

	/* Person types */
	const representative = 'representative';
	const delegate = 'delegate';

	/* JSON to send back in Attendance section */
	const {
		formData,
		setFormData,
		clearFormData
	} = React.useContext(MainAppContext);

	/* Checks if the form is being sent */
	const [state, setState] = React.useState({
		sending: false,
		result: 0
	});

	/* Validation context */
	const {
		hasErrors, showValidation, setShowValidation, revalidateInputs
	} = React.useContext(ValidationContext);

	/* Set validation context */
	React.useEffect(() => {
		setState({
			...state,
			result: 0,
			open: false
		});
	}, []);

	/* Reset formData if formType changes */
	React.useEffect(() => {
		clearFormData();
		setShowValidation(false);
		// Default vote values
		if (
			(portal?.council?.agendas?.length > 0
				&& portal?.config?.optionalFields?.[formType]?.earlyVote)
			|| (formType === 'earlyVote')
		) {
			setFormData({
				...formData,
				votes: Object.values(portal?.config?.defaultVoteSense?.custom)
			});
		}
	}, [formType]);

	/* Default values for the styles */
	const defaultStyleDIV = {
		backgroundColor: colors?.background,
	};
	const defaultStyleBLOCK = {
		borderLeft: width > WINDOW_SIZE.md && `2px solid ${colors?.primary}`
	};
	/* On submit */
	const createRequest = async () => {
		setState({
			...state,
			sending: true
		});

		const getRequestType = () => {
			if (formType === 'representation') {
				return 'representation';
			}
			if (formType === 'delegation') {
				return 'represent';
			}
			if (formType === 'earlyVote') {
				return 'vote';
			}
			if (formType === 'interventions') {
				return 'intervention';
			}
			return formData.personOrEntity === 0 ? 'access' : 'representation';
		};

		function getVoteSense() {
			return (formType === 'earlyVote' || (portal?.config?.optionalFields[formType]?.earlyVote === true && formData?.votesIsActive));
		}

		const request = {
			...formData,
			requestType: getRequestType(),
			attachments: Object.values(formData.attachments),
			councilId: portal.councilId,
			representative: !formData.representative.name ? null : formData.representative,
			delegate: !formData.delegate.name ? null : formData.delegate,
			socialCapital: formData.socialCapital ? formData.socialCapital : null,
			numParticipations: formData.numParticipations ? formData.numParticipations : null,
			position: formData.position ? formData.position : null,
			votes: getVoteSense() ? formData?.votes?.sort(((a, b) => a.orderIndex - b.orderIndex)) : null,
		};
		const response = await client.mutate({
			mutation: gql`
				mutation CreateRequest($request: SequelizeJSON){
					createShareholderRequest(request: $request){
						id
					}
				}
			`,
			variables: {
				request: {
					...request
				}
			}
		});
		setState({
			...state,
			sending: false
		});
		if (response?.data?.createShareholderRequest?.id) {
			setState({
				...state,
				result: 1
			});
			setShowValidation(false);
			clearFormData();
		} else if (response?.errors[0]?.code === 701) {
			setState({
				...state,
				result: 3,
				open: true
			});
		} else if (response?.errors[0]?.code === 702) {
			setState({
				...state,
				result: 4,
				open: true
			});
		} else if (response?.errors[0]?.code === 703) {
			setState({
				...state,
				result: 5,
				open: true
			});
		} else if (response?.errors) {
			setState(oldState => ({
				...oldState,
				result: 2,
				open: true
			}));
		}
	};
	/* On submit function controlling the validation */
	const onSubmit = () => {
		/* First time clicking, if errors are found */
		if (formData.delegate.name === '' && portal.name === 'mutualmedica' && formType === 'delegation') {
			setShowValidation(true);
			return;
		}
		if (!showValidation && hasErrors) {
			setShowValidation(true);
			return;
		}
		revalidateInputs();
		if (hasErrors) {
			return;
		}
		setState({
			...state,
			result: 0,
			open: true

		});
	};

	if (
		(portal.config.portalDates.open.option === 2 && portal.config.portalDates.open?.[formType]?.date > dateString)
		|| (portal.config.portalDates.close.option === 2 && portal.config.portalDates.close?.[formType]?.date < dateString)
	) {
		return <ClosedSection
			colors={colors}
			portal={portal}
			section={portal?.config?.sections?.[formType]}
			translate={translate}
			updateRouting={updateRouting}
		/>;
	}

	return <div
		className={classNameDIV}
		style={defaultStyleDIV}
	>
		{/* SHAREHOLDER DATA, COMMON TO EVERY FORM */}
		<ShareholderData
			colors={colors}
			formType={formType}
			portal={portal}
			formData={formData}
			setFormData={setFormData}
			translate={translate}
			styleLIST={defaultStyleBLOCK}
		/>

		{/* Representation */}
		{((formType === 'access' && !!formData?.personOrEntity)
			|| (formType === 'representation'))
			&& <RepresentativeOrDelegateForm
				colors={colors}
				formType={formType}
				personType={representative}
				portal={portal}
				formData={formData}
				setFormData={setFormData}
				translate={translate}
				styleLIST={defaultStyleBLOCK}
			/>}

		{/* Representation IN OTHER SECTIONS */}
		{portal?.config?.optionalFields?.[formType]?.representation
			&& <RepresentativeOrDelegateForm
				colors={colors}
				isOptional={true}
				formType={formType}
				personType={representative}
				portal={portal}
				formData={formData}
				setFormData={setFormData}
				translate={translate}
				styleLIST={defaultStyleBLOCK}
			/>}
		{/* CPORT-123 HABILITAR SI HAY LISTADO DE REPRESENTANTES */}
		{formType === 'delegation' && portal?.name === 'mutualmedica'
		&& <Representatives
			colors={colors}
			translate={translate}
			setFormData={setFormData}
			formData={formData}
			portal={portal}
			isOptional={true}
			formType={formType}
			personType={delegate}
		/>}
		{/* DELEGATION */}
		{(formType === 'delegation')
			&& <RepresentativeOrDelegateForm
				colors={colors}
				formType={formType}
				personType={delegate}
				portal={portal}
				formData={formData}
				setFormData={setFormData}
				translate={translate}
				styleLIST={defaultStyleBLOCK}
			/>}
		{/* DELEGATION IN OTHER SECTIONS */}
		{portal?.config?.optionalFields?.[formType]?.delegation
			&& <RepresentativeOrDelegateForm
				colors={colors}
				isOptional={true}
				formType={formType}
				personType={delegate}
				portal={portal}
				formData={formData}
				setFormData={setFormData}
				translate={translate}
				styleLIST={defaultStyleBLOCK}
			/>}

		{/* EARLY VOTE */}
		{portal?.council?.agendas?.length > 0
			&& (formType === 'earlyVote')
			&& <EarlyVoteForm
				portal={portal}
				formData={formData}
				setFormData={setFormData}
				colors={colors}
				translate={translate}
				styleLIST={defaultStyleBLOCK}
			/>}

		{/* EARLY VOTE IN OTHER SECTIONS */}
		{portal?.council?.agendas?.length > 0
			&& portal?.config?.optionalFields?.[formType]?.earlyVote
			&& <EarlyVoteForm
				classNameDIV='pt-4 space-y-5'
				portal={portal}
				isOptional={true}
				formData={formData}
				setFormData={setFormData}
				colors={colors}
				translate={translate}
				styleLIST={defaultStyleBLOCK}
			/>}


		{/* INTERVENTION */}
		{formType === 'interventions'
			&& <InterventionsForm
				portal={portal}
				formData={formData}
				setFormData={setFormData}
				colors={colors}
				translate={translate}
			/>}


		{/* INTERVENTION IN OTHER SECTIONS */}
		{portal?.config?.optionalFields?.[formType]?.interventions
			&& <InterventionsForm
				portal={portal}
				isOptional={true}
				formData={formData}
				setFormData={setFormData}
				colors={colors}
				translate={translate}
			/>}

		{/* TEXT */}
		{portal?.config?.forms?.aditionalText?.isActive
			&& portal?.config?.aditionalTextFields?.[formType]?.aditionalText && <AditionalText
			id='Text'
			portal={portal}
			text={portal?.config?.forms?.aditionalText}
			translate={translate}
			colors={colors}
		/>
		}
		{/* SEND BUTTON */}
		<div className='float-right mt-16 flex justify-end'>
			<SimpleButton
				classNameBTN='mt-16 px-10 cursor-pointer py-4 focus:outline-none text-sm md:text-lg bold rounded-md md:w-max w-full'
				colors={colors}
				disabled={(hasErrors && showValidation) || state.sending || ((!formData.delegate || formData.delegate.name === '') && portal.name === 'mutualmedica' && formType === 'delegation')}
				tag={getText(portal?.config?.forms?.send, translate)}
				loading={state.sending}
				onClick={() => onSubmit()}
			/>
		</div>

		{/* SEND FORM POPUP */}
		<PopUpWrapper
			colors={colors}
			open={state.open}
			setOpen={value => {
				setState({
					...state,
					open: value
				});
			}}
		>
			<SendFormPopUp
				formData={formData}
				portal={portal}
				formType={formType}
				translate={translate}
				colors={colors}
				sending={state.sending}
				result={state.result}
				onConfirm={() => createRequest()}
				setOpen={value => {
					setState({
						...state,
						open: value
					});
				}}
				onSuccess={() => {
					onSuccess();
					setState({
						...state,
						open: false
					});
				}}
			/>
		</PopUpWrapper>

	</div>;
}

export default FormFactory;
